import React, { useState, useEffect, useContext } from 'react';
import { motion } from 'framer-motion';
import { Toaster, toast } from 'react-hot-toast';
import {
  TruckIcon,
  XCircleIcon,
  CurrencyDollarIcon,
  ExclamationIcon,
  ChartBarIcon,
  RefreshIcon,
  DocumentTextIcon,
  PlusIcon,
  ArrowsExpandIcon,
} from '@heroicons/react/solid';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner } from '@fortawesome/free-solid-svg-icons';
import { Line } from 'react-chartjs-2';
import { Chart } from 'chart.js';
import {
  CategoryScale,
  LinearScale,
  LineController,
  LineElement,
  PointElement,
  Tooltip,
  Legend,
} from 'chart.js';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import axios from 'axios';
import { BASE_URL } from '../../config';
import { RateLimitAlertContext } from '../../contexts/RateLimitAlertContext';
import { AdminAccessAlertContext } from '../../contexts/AdminAccessAlertContext';

Chart.register(
  CategoryScale,
  LinearScale,
  LineController,
  LineElement,
  PointElement,
  Tooltip,
  Legend
);

const CollectionDashboard = () => {
  const [metrics, setMetrics] = useState({
    totalBilledDelivered: 0,
    totalBilledUndelivered: 0,
    totalCollected: 0,
    totalUnpaid: 0,
  });
  const [chartData, setChartData] = useState(null);
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reportsLoading, setReportsLoading] = useState(false);
  const [generateModalOpen, setGenerateModalOpen] = useState(false);
  const [generateForm, setGenerateForm] = useState({
    reportType: 'summary',
    dateFrom: '',
    dateTo: '',
  });
  const [timeFrame, setTimeFrame] = useState('7');
  const [reportFilters, setReportFilters] = useState({
    reportType: '',
    dateFrom: '',
    dateTo: '',
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(5);
  const [totalPages, setTotalPages] = useState(1);
  const [totalEntries, setTotalEntries] = useState(0);
  const [selectedReport, setSelectedReport] = useState(null);
  const [isFullScreen, setIsFullScreen] = useState(false);
  const { openAlert: openRateLimitAlert } = useContext(RateLimitAlertContext);
  const { openAlert: openAdminAccessAlert } = useContext(AdminAccessAlertContext);

  // Add to existing state declarations
  const [tempDateFilters, setTempDateFilters] = useState({
    dateFrom: '',
    dateTo: '',
  });

  const fetchData = async (period) => {
    setLoading(true);
    try {
      const token = localStorage.getItem('authentication/token');
      const [metricsRes, trendsRes, reportsRes] = await Promise.all([
        axios.get(`${BASE_URL}/collections/dashboard/metrics`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        axios.get(`${BASE_URL}/collections/dashboard/trends`, {
          headers: { Authorization: `Bearer ${token}` },
          params: { days: period },
        }),
        axios.get(`${BASE_URL}/collections/dashboard/billing-analysis-reports`, {
          headers: { Authorization: `Bearer ${token}` },
          params: {
            report_type: reportFilters.reportType || undefined,
            date_from: reportFilters.dateFrom || undefined,
            date_to: reportFilters.dateTo || undefined,
            page: currentPage,
            per_page: itemsPerPage,
          },
        }),
      ]);

      setMetrics(metricsRes.data.data);
      prepareChartData(trendsRes.data.data, period);
      setReports(reportsRes.data.data);
      setTotalEntries(reportsRes.data.pagination.total_records);
      setTotalPages(reportsRes.data.pagination.total_pages);
    } catch (error) {
      if (error.response?.status === 401) window.location.href = '/login';
      if (error.response?.status === 403) openAdminAccessAlert();
      if (error.response?.status === 429) openRateLimitAlert();
      console.error('Error fetching dashboard data:', error);
      toast.error('Failed to fetch dashboard data.', { duration: 4000 });
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateReport = async () => {
    setReportsLoading(true);
    try {
      const token = localStorage.getItem('authentication/token');
      await axios.post(
        `${BASE_URL}/collections/dashboard/generate-analysis-report`,
        {
          report_type: generateForm.reportType,
          date_from: generateForm.dateFrom || undefined,
          date_to: generateForm.dateTo || undefined,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Report generation queued. Refresh to see the new report.', { duration: 4000 });
      setGenerateModalOpen(false);
      // Refresh reports after a short delay to allow processing
      setTimeout(() => fetchData(timeFrame), 2000);
    } catch (error) {
      if (error.response?.status === 401) window.location.href = '/login';
      if (error.response?.status === 403) openAdminAccessAlert();
      if (error.response?.status === 429) openRateLimitAlert();
      toast.error('Failed to generate report.', { duration: 4000 });
    } finally {
      setReportsLoading(false);
    }
  };

  const handleGenerateFormChange = (key, value) => {
    setGenerateForm((prev) => ({ ...prev, [key]: value }));
  };

  useEffect(() => {
    fetchData(timeFrame);
  }, [timeFrame, reportFilters, currentPage, itemsPerPage]);

  const formatCurrency = (num) => {
    return new Intl.NumberFormat('en-PH', {
      style: 'currency',
      currency: 'PHP',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(num || 0);
  };

  const prepareChartData = (trends, days) => {
    const labels = [];
    const endDate = new Date();
    const startDate = new Date();
    startDate.setDate(endDate.getDate() - parseInt(days));

    for (let d = new Date(startDate); d <= endDate; d.setDate(d.getDate() + 1)) {
      labels.push(new Date(d).toLocaleDateString('en-PH', { month: 'short', day: 'numeric' }));
    }

    const datasets = {
      totalBilledDelivered: Array(labels.length).fill(0),
      totalBilledUndelivered: Array(labels.length).fill(0),
      totalCollected: Array(labels.length).fill(0),
      totalUnpaid: Array(labels.length).fill(0),
    };

    trends.forEach((item) => {
      const dateIndex = labels.indexOf(
        new Date(item.date).toLocaleDateString('en-PH', { month: 'short', day: 'numeric' })
      );
      if (dateIndex !== -1) {
        datasets.totalBilledDelivered[dateIndex] = item.total_billed_delivered;
        datasets.totalBilledUndelivered[dateIndex] = item.total_billed_undelivered;
        datasets.totalCollected[dateIndex] = item.total_collected;
        datasets.totalUnpaid[dateIndex] = item.total_unpaid;
      }
    });

    setChartData({
      labels,
      datasets: [
        {
          label: 'Total Billed Delivered',
          data: datasets.totalBilledDelivered,
          borderColor: '#10b981',
          backgroundColor: 'rgba(16, 185, 129, 0.1)',
          tension: 0.4,
          borderWidth: 2,
          pointRadius: 3,
          pointHoverRadius: 6,
        },
        {
          label: 'Total Billed Undelivered',
          data: datasets.totalBilledUndelivered,
          borderColor: '#ef4444',
          backgroundColor: 'rgba(239, 68, 68, 0.1)',
          tension: 0.4,
          borderWidth: 2,
          pointRadius: 3,
          pointHoverRadius: 6,
        },
        {
          label: 'Total Collected',
          data: datasets.totalCollected,
          borderColor: '#3b82f6',
          backgroundColor: 'rgba(59, 130, 246, 0.1)',
          tension: 0.4,
          borderWidth: 2,
          pointRadius: 3,
          pointHoverRadius: 6,
        },
        {
          label: 'Total Unpaid',
          data: datasets.totalUnpaid,
          borderColor: '#f59e0b',
          backgroundColor: 'rgba(245, 158, 11, 0.1)',
          tension: 0.4,
          borderWidth: 2,
          pointRadius: 3,
          pointHoverRadius: 6,
        },
      ],
    });
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
        labels: {
          color: '#1f2937',
          font: { size: 12, family: 'Inter, sans-serif', weight: '500' },
          padding: 15,
          boxWidth: 20,
        },
      },
      tooltip: {
        backgroundColor: '#1f2937',
        titleColor: '#fff',
        bodyColor: '#fff',
        padding: 12,
        titleFont: { size: 12, family: 'Inter, sans-serif' },
        bodyFont: { size: 12, family: 'Inter, sans-serif' },
        displayColors: true,
        callbacks: {
          label: (context) => `${context.dataset.label}: ${formatCurrency(context.parsed.y)}`,
        },
      },
    },
    scales: {
      x: {
        grid: { display: false },
        ticks: { color: '#6b7280', font: { size: 11, family: 'Inter, sans-serif' }, maxRotation: 45, autoSkip: true },
      },
      y: {
        beginAtZero: true,
        grid: { color: '#e5e7eb' },
        ticks: {
          color: '#6b7280',
          font: { size: 11, family: 'Inter, sans-serif' },
          callback: (value) => formatCurrency(value).replace('PHP', ''),
        },
      },
    },
    interaction: {
      mode: 'index',
      intersect: false,
    },
  };

  const cardData = [
    {
      icon: TruckIcon,
      color: 'from-green-500 to-emerald-600',
      title: 'Total Billed Delivered',
      value: metrics.totalBilledDelivered,
    },
    {
      icon: XCircleIcon,
      color: 'from-red-500 to-rose-600',
      title: 'Total Billed Undelivered',
      value: metrics.totalBilledUndelivered,
    },
    {
      icon: CurrencyDollarIcon,
      color: 'from-blue-500 to-indigo-600',
      title: 'Total Collected',
      value: metrics.totalCollected,
    },
    {
      icon: ExclamationIcon,
      color: 'from-amber-500 to-orange-600',
      title: 'Total Unpaid',
      value: metrics.totalUnpaid,
    },
  ];

  const timeFrames = [
    { value: '7', label: '7 Days' },
    { value: '14', label: '14 Days' },
    { value: '21', label: '21 Days' },
    { value: '30', label: '1 Month' },
  ];

  // const handleFilterChange = (key, value) => {
  //   setReportFilters((prev) => ({ ...prev, [key]: value }));
  //   setCurrentPage(1);
  // };
  const handleFilterChange = (key, value) => {
    if (key === 'reportType') {
      setReportFilters((prev) => ({ ...prev, [key]: value }));
      setCurrentPage(1);
    } else {
      setTempDateFilters((prev) => ({ ...prev, [key]: value }));
    }
  };

  const handleApplyDateFilters = () => {
    setReportFilters((prev) => ({
      ...prev,
      dateFrom: tempDateFilters.dateFrom,
      dateTo: tempDateFilters.dateTo,
    }));
    setCurrentPage(1);
  };

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) setCurrentPage(page);
  };

  const handleItemsPerPageChange = (value) => {
    setItemsPerPage(value);
    setCurrentPage(1);
  };

  const handleDownloadReport = (reportId, reportType, createdAt) => {
    const report = reports.find((r) => r.report_id === reportId);
    if (!report) return;

    const blob = new Blob([report.ai_report], { type: 'text/plain' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `Billing_Analysis_${reportType}_${createdAt.replace(/[: ]/g, '-')}.txt`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
    toast.success('Report downloaded successfully.', { duration: 4000 });
  };

  const handleViewReport = (reportId) => {
    const report = reports.find((r) => r.report_id === reportId);
    setSelectedReport(report);
    setIsFullScreen(false); // Reset to default size when opening
  };

  const toggleFullScreen = () => {
    setIsFullScreen((prev) => !prev);
  };

  const getShowingEntriesText = () => {
    const start = (currentPage - 1) * itemsPerPage + 1;
    const end = Math.min(currentPage * itemsPerPage, totalEntries);
    return `Showing ${start} to ${end} of ${totalEntries.toLocaleString()} reports`;
  };

  const Skeleton = () => (
    <div className="animate-pulse space-y-8">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {[...Array(4)].map((_, i) => (
          <div key={i} className="h-32 bg-gray-200 rounded-2xl"></div>
        ))}
      </div>
      <div className="h-48 bg-gray-200 rounded-2xl"></div>
      <div className="h-96 bg-gray-200 rounded-2xl"></div>
    </div>
  );

  const ReportSkeleton = () => (
    <div className="animate-pulse">
      <div className="h-12 bg-gray-200 rounded-lg mb-4"></div>
      <div className="space-y-2">
        {[...Array(itemsPerPage)].map((_, i) => (
          <div key={i} className="h-10 bg-gray-200 rounded-lg"></div>
        ))}
      </div>
    </div>
  );

  const cardVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.4 } },
  };

  const sectionVariants = {
    hidden: { opacity: 0, scale: 0.95 },
    visible: { opacity: 1, scale: 1, transition: { duration: 0.5 } },
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-100 to-gray-200 p-6 font-inter">
      <Toaster position="top-right" toastOptions={{ duration: 4000 }} />
      <link
        href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap"
        rel="stylesheet"
      />
      <div className="max-w-1xl mx-auto space-y-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <motion.div
              className="p-3 bg-white rounded-full shadow-lg"
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.95 }}
            >
              <ChartBarIcon className="h-6 w-6 text-blue-600" />
            </motion.div>
            <div>
              <h1 className="text-3xl font-bold text-gray-900 tracking-tight">
                Financial Dashboard
              </h1>
              <p className="text-sm text-gray-600 mt-1">
                Real-time insights into your billing and collection performance
              </p>
            </div>
          </div>
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => fetchData(timeFrame)}
            className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-lg shadow-md hover:from-blue-700 hover:to-indigo-700 transition-all duration-300 text-sm font-medium"
          >
            <RefreshIcon className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </motion.button>
        </div>

        {/* Metrics Grid */}
        {loading ? (
          <Skeleton />
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {cardData.map((card, index) => (
                <motion.div
                  key={index}
                  variants={cardVariants}
                  initial="hidden"
                  animate="visible"
                  className={`bg-white/90 backdrop-blur-md bg-gradient-to-r ${card.color} text-white rounded-2xl shadow-xl p-6 transform transition hover:scale-105 hover:shadow-2xl`}
                >
                  <div className="flex items-center gap-4">
                    <div className="p-4 bg-white bg-opacity-20 rounded-full">
                      <card.icon className="h-6 w-6" />
                    </div>
                    <div>
                      <h3 className="text-sm font-medium opacity-90">{card.title}</h3>
                      <p className="text-2xl font-semibold mt-1">{formatCurrency(card.value)}</p>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>

            {/* AI Billing Analysis Section */}
            <motion.div
              variants={sectionVariants}
              initial="hidden"
              animate="visible"
              className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl shadow-md p-6 border border-indigo-100"
            >
              {/* <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
                <div className="flex items-center gap-3">
                  <DocumentTextIcon className="h-6 w-6 text-indigo-600" />
                  <h2 className="text-lg font-semibold text-gray-800">
                    AI Billing Analysis Reports
                  </h2>
                </div>
                <div className="flex flex-wrap gap-2">
                  <select
                    value={reportFilters.reportType}
                    onChange={(e) => handleFilterChange('reportType', e.target.value)}
                    className="px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-1 focus:ring-indigo-500"
                  >
                    <option value="">All Reports</option>
                    <option value="summary">Summary</option>
                    <option value="detailed">Detailed</option>
                  </select>
                  <input
                    type="date"
                    value={reportFilters.dateFrom}
                    onChange={(e) => handleFilterChange('dateFrom', e.target.value)}
                    className="px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-1 focus:ring-indigo-500"
                    placeholder="From"
                  />
                  <input
                    type="date"
                    value={reportFilters.dateTo}
                    onChange={(e) => handleFilterChange('dateTo', e.target.value)}
                    className="px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-1 focus:ring-indigo-500"
                    placeholder="To"
                  />
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => setGenerateModalOpen(true)}
                    className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-lg shadow-md hover:from-green-700 hover:to-emerald-700 transition-all duration-300 text-sm font-medium"
                  >
                    <PlusIcon className="h-4 w-4" />
                    Generate Report
                  </motion.button>
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => fetchData(timeFrame)}
                    className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-lg shadow-md hover:from-indigo-700 hover:to-purple-700 transition-all duration-300 text-sm font-medium"
                  >
                    <RefreshIcon className={`h-4 w-4 ${reportsLoading ? 'animate-spin' : ''}`} />
                    Refresh Reports
                  </motion.button>
                </div>
              </div> */}

<div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
  <div className="flex items-center gap-3">
    <DocumentTextIcon className="h-6 w-6 text-indigo-600" />
    <h2 className="text-lg font-semibold text-gray-800">
      AI Billing Analysis Reports
    </h2>
  </div>
  <div className="flex flex-wrap gap-2">
    <select
      value={reportFilters.reportType}
      onChange={(e) => handleFilterChange('reportType', e.target.value)}
      className="px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-1 focus:ring-indigo-500"
    >
      <option value="">All Reports</option>
      <option value="summary">Summary</option>
      <option value="detailed">Detailed</option>
    </select>
    <input
      type="date"
      value={tempDateFilters.dateFrom}
      onChange={(e) => handleFilterChange('dateFrom', e.target.value)}
      className="px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-1 focus:ring-indigo-500"
      placeholder="From"
    />
    <input
      type="date"
      value={tempDateFilters.dateTo}
      onChange={(e) => handleFilterChange('dateTo', e.target.value)}
      className="px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-1 focus:ring-indigo-500"
      placeholder="To"
    />
    <motion.button
      whileHover={{ scale: 1.05 }}
      whileTap={{ scale: 0.95 }}
      onClick={handleApplyDateFilters}
      className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-lg shadow-md hover:from-blue-700 hover:to-indigo-700 transition-all duration-300 text-sm font-medium"
    >
      Apply Filter
    </motion.button>
    <motion.button
      whileHover={{ scale: 1.05 }}
      whileTap={{ scale: 0.95 }}
      onClick={() => setGenerateModalOpen(true)}
      className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-lg shadow-md hover:from-green-700 hover:to-emerald-700 transition-all duration-300 text-sm font-medium"
    >
      <PlusIcon className="h-4 w-4" />
      Generate Report
    </motion.button>
    <motion.button
      whileHover={{ scale: 1.05 }}
      whileTap={{ scale: 0.95 }}
      onClick={() => fetchData(timeFrame)}
      className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-lg shadow-md hover:from-indigo-700 hover:to-purple-700 transition-all duration-300 text-sm font-medium"
    >
      <RefreshIcon className={`h-4 w-4 ${reportsLoading ? 'animate-spin' : ''}`} />
      Refresh Reports
    </motion.button>
  </div>
</div>



              <div className="overflow-x-auto">
                {reportsLoading ? (
                  <ReportSkeleton />
                ) : reports.length === 0 ? (
                  <div className="text-center text-gray-500 py-4">
                    No AI reports found. Try generating a new report or adjusting the filters.
                  </div>
                ) : (
                  <table className="w-full text-sm text-gray-700">
                    <thead className="bg-indigo-100">
                      <tr>
                        <th className="p-3 text-left font-semibold text-gray-600">Report ID</th>
                        <th className="p-3 text-left font-semibold text-gray-600">Type</th>
                        <th className="p-3 text-left font-semibold text-gray-600">Date From</th>
                        <th className="p-3 text-left font-semibold text-gray-600">Date To</th>
                        <th className="p-3 text-left font-semibold text-gray-600">Created At</th>
                        <th className="p-3 text-left font-semibold text-gray-600">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {reports.map((report) => (
                        <motion.tr
                          key={report.report_id}
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          transition={{ duration: 0.3 }}
                          className="hover:bg-indigo-50"
                        >
                          <td className="p-3">{report.report_id}</td>
                          <td className="p-3">{report.report_type.charAt(0).toUpperCase() + report.report_type.slice(1)}</td>
                          <td className="p-3">{report.date_from || '-'}</td>
                          <td className="p-3">{report.date_to || '-'}</td>
                          <td className="p-3">{report.created_at}</td>
                          <td className="p-3 flex gap-2">
                            <motion.button
                              whileHover={{ scale: 1.1 }}
                              whileTap={{ scale: 0.95 }}
                              onClick={() => handleViewReport(report.report_id)}
                              className="text-indigo-600 hover:text-indigo-800"
                            >
                              View
                            </motion.button>
                            <motion.button
                              whileHover={{ scale: 1.1 }}
                              whileTap={{ scale: 0.95 }}
                              onClick={() => handleDownloadReport(report.report_id, report.report_type, report.created_at)}
                              className="text-indigo-600 hover:text-indigo-800"
                            >
                              <DocumentTextIcon className="h-5 w-5" />
                            </motion.button>
                          </td>
                        </motion.tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
              <div className="flex flex-col sm:flex-row justify-between items-center mt-4 gap-3">
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <select
                    value={itemsPerPage}
                    onChange={(e) => handleItemsPerPageChange(Number(e.target.value))}
                    className="px-3 py-2 border border-gray-200 rounded-lg focus:ring-1 focus:ring-indigo-500"
                  >
                    {[5, 10, 20].map((val) => (
                      <option key={val} value={val}>{val}/page</option>
                    ))}
                  </select>
                  <span>{getShowingEntriesText()}</span>
                </div>
                <div className="flex items-center gap-2">
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1 || reportsLoading}
                    className="p-2 text-gray-600 rounded-lg hover:bg-indigo-100 disabled:opacity-50"
                  >
                    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
                    </svg>
                  </motion.button>
                  <span className="text-sm text-gray-600">{currentPage} of {totalPages}</span>
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages || reportsLoading}
                    className="p-2 text-gray-600 rounded-lg hover:bg-indigo-100 disabled:opacity-50"
                  >
                    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                    </svg>
                  </motion.button>
                </div>
              </div>
            </motion.div>

            {/* Generate Report Modal */}
            {generateModalOpen && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50"
              >
                <motion.div
                  initial={{ scale: 0.95, y: 20 }}
                  animate={{ scale: 1, y: 0 }}
                  className="bg-white rounded-2xl shadow-xl p-6 w-full max-w-md"
                >
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="text-lg font-semibold text-gray-800">Generate AI Report</h3>
                    <button
                      onClick={() => setGenerateModalOpen(false)}
                      className="text-gray-600 hover:text-gray-800 text-lg"
                    >
                      ×
                    </button>
                  </div>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Report Type</label>
                      <select
                        value={generateForm.reportType}
                        onChange={(e) => handleGenerateFormChange('reportType', e.target.value)}
                        className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-1 focus:ring-indigo-500"
                      >
                        <option value="summary">Summary</option>
                        <option value="detailed">Detailed</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Date From</label>
                      <input
                        type="date"
                        value={generateForm.dateFrom}
                        onChange={(e) => handleGenerateFormChange('dateFrom', e.target.value)}
                        className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-1 focus:ring-indigo-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Date To</label>
                      <input
                        type="date"
                        value={generateForm.dateTo}
                        onChange={(e) => handleGenerateFormChange('dateTo', e.target.value)}
                        className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-1 focus:ring-indigo-500"
                      />
                    </div>
                    <div className="flex justify-end gap-2">
                      <motion.button
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        onClick={() => setGenerateModalOpen(false)}
                        className="px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300"
                        disabled={reportsLoading}
                      >
                        Cancel
                      </motion.button>
                      <motion.button
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        onClick={handleGenerateReport}
                        className="px-4 py-2 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-lg hover:from-green-700 hover:to-emerald-700"
                        disabled={reportsLoading}
                      >
                        {reportsLoading ? (
                          <FontAwesomeIcon icon={faSpinner} className="animate-spin" />
                        ) : (
                          'Generate'
                        )}
                      </motion.button>
                    </div>
                  </div>
                </motion.div>
              </motion.div>
            )}

            {/* Report Preview Modal */}
            {selectedReport && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50"
            >
              <motion.div
                initial={{ scale: 0.95, y: 20 }}
                animate={{ scale: 1, y: 0 }}
                className={`bg-white rounded-2xl shadow-xl p-8 w-full ${isFullScreen ? 'max-w-[95vw] max-h-[95vh]' : 'max-w-4xl max-h-[80vh]'} overflow-y-auto`}
              >
                <div className="flex justify-between items-center mb-6">
                  <h3 className="text-xl font-semibold text-gray-800">
                    {selectedReport.report_type.charAt(0).toUpperCase() + selectedReport.report_type.slice(1)} Report
                  </h3>
                  <div className="flex items-center gap-2">
                    <motion.button
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={toggleFullScreen}
                      className="text-gray-600 hover:text-gray-800"
                    >
                      <ArrowsExpandIcon className="h-6 w-6" />
                    </motion.button>
                    <button
                      onClick={() => setSelectedReport(null)}
                      className="text-gray-600 hover:text-gray-800 text-xl"
                    >
                      ×
                    </button>
                  </div>
                </div>
                <div className="text-base text-gray-700 prose prose-indigo max-w-none leading-relaxed">
                  <ReactMarkdown
                    remarkPlugins={[remarkGfm]}
                    rehypePlugins={[rehypeRaw]}
                    components={{
                      p: ({ node, ...props }) => <p className="mb-4" {...props} />,
                      ul: ({ node, ...props }) => <ul className="list-disc list-inside mb-4" {...props} />,
                      ol: ({ node, ...props }) => <ol className="list-decimal list-inside mb-4" {...props} />,
                      li: ({ node, ...props }) => <li className="mb-2" {...props} />,
                      h1: ({ node, ...props }) => <h1 className="text-2xl font-bold text-gray-900 mb-4" {...props} />,
                      h2: ({ node, ...props }) => <h2 className="text-xl font-semibold text-gray-800 mb-3" {...props} />,
                      h3: ({ node, ...props }) => <h3 className="text-lg font-semibold text-gray-800 mb-2" {...props} />,
                      table: ({ node, ...props }) => (
                        <div className="overflow-x-auto mb-4">
                          <table className="w-full border-collapse border border-gray-300" {...props} />
                        </div>
                      ),
                      thead: ({ node, ...props }) => <thead className="bg-indigo-100" {...props} />,
                      th: ({ node, ...props }) => <th className="border border-gray-300 p-2 text-left font-semibold text-gray-600" {...props} />,
                      td: ({ node, ...props }) => <td className="border border-gray-300 p-2" {...props} />,
                      code: ({ node, inline, ...props }) => (
                        inline ? (
                          <code className="bg-gray-100 rounded px-1" {...props} />
                        ) : (
                          <code className="block bg-gray-100 rounded p-2 mb-4" {...props} />
                        )
                      ),
                    }}
                  >
                    {selectedReport.ai_report}
                  </ReactMarkdown>
                </div>
                <div className="mt-6 flex justify-end">
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => handleDownloadReport(selectedReport.report_id, selectedReport.report_type, selectedReport.created_at)}
                    className="px-4 py-2 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-lg shadow-md hover:from-indigo-700 hover:to-purple-700"
                  >
                    Download Report
                  </motion.button>
                </div>
              </motion.div>
            </motion.div>
          )}

            {/* Financial Trends Chart */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="bg-white/95 backdrop-blur-md rounded-2xl shadow-md p-6 border border-gray-100"
            >
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
                <div className="flex items-center gap-3">
                  <ChartBarIcon className="h-5 w-5 text-blue-600" />
                  <h2 className="text-lg font-semibold text-gray-800">
                    Financial Trends ({timeFrames.find((tf) => tf.value === timeFrame)?.label})
                  </h2>
                </div>
                <div className="flex flex-wrap gap-2">
                  {timeFrames.map((tf) => (
                    <motion.button
                      key={tf.value}
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={() => setTimeFrame(tf.value)}
                      className={`px-4 py-2 text-sm font-medium rounded-lg transition-all duration-200 ${
                        timeFrame === tf.value
                          ? 'bg-gradient-to-r from-blue-600 to-indigo-600 text-white shadow-sm'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      {tf.label}
                    </motion.button>
                  ))}
                </div>
              </div>
              <div className="h-96">
                {chartData ? (
                  <Line data={chartData} options={chartOptions} />
                ) : (
                  <div className="h-full flex items-center justify-center text-gray-500">
                    No data available
                  </div>
                )}
              </div>
            </motion.div>
          </>
        )}

        {loading && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 flex flex-col items-center gap-4">
              <FontAwesomeIcon icon={faSpinner} className="text-indigo-600 text-2xl animate-spin" />
              <p className="text-gray-800 text-lg">Loading dashboard...</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CollectionDashboard;